import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, BarChart3, DollarSign, Clock, AlertCircle, Search, Star, StarOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Badge } from '@vaiu/ui/badge';
import { Progress } from '@vaiu/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { unifiedTokenService } from '@/services/UnifiedTokenService';
import { useAuth } from '@/context/AuthContext';
import type { Token, Order, MarketData } from '@/types/token';
import DevelopmentNotice from '@/components/DevelopmentNotice';

// Popular stocks with their metadata
const POPULAR_STOCKS = [
  { symbol: 'AAPL', name: 'Apple Inc.', sector: 'Technology' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', sector: 'Technology' },
  { symbol: 'MSFT', name: 'Microsoft Corp.', sector: 'Technology' },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer' },
  { symbol: 'TSLA', name: 'Tesla Inc.', sector: 'Automotive' },
  { symbol: 'META', name: 'Meta Platforms', sector: 'Technology' },
  { symbol: 'NVDA', name: 'NVIDIA Corp.', sector: 'Technology' },
  { symbol: 'JPM', name: 'JPMorgan Chase', sector: 'Finance' }
];

interface StockWithData extends Token {
  marketData?: MarketData;
  userPosition?: {
    shares: number;
    avgCost: number;
    currentValue: number;
    profit: number;
    profitPercent: number;
  };
}

export default function StockExchangePage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedStock, setSelectedStock] = useState<StockWithData | null>(null);
  const [stocks, setStocks] = useState<StockWithData[]>([]);
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [portfolio, setPortfolio] = useState<StockWithData[]>([]);
  const [openOrders, setOpenOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Order form state
  const [orderForm, setOrderForm] = useState({
    action: 'buy' as 'buy' | 'sell',
    orderType: 'market' as 'market' | 'limit' | 'stop',
    shares: '',
    price: '',
    stopPrice: ''
  });

  useEffect(() => {
    loadStocks();
    loadPortfolio();
    loadOrders();
    const interval = setInterval(() => {
      loadStocks();
      if (selectedStock) {
        loadStockDetails(selectedStock.symbol);
      }
    }, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const loadStocks = async () => {
    try {
      const symbols = POPULAR_STOCKS.map(s => `S.${s.symbol}`);
      const marketData = await unifiedTokenService.getMarketPrices(symbols);
      
      const stocksWithData: StockWithData[] = POPULAR_STOCKS.map((stock, index) => ({
        id: `S.${stock.symbol}`,
        type: 'stock' as const,
        symbol: `S.${stock.symbol}`,
        metadata: {
          name: stock.name,
          symbol: stock.symbol,
          company: stock.name,
          sector: stock.sector,
          exchange: 'VAIU'
        },
        supply: {
          initial: 1000000,
          current: 1000000,
          maximum: 1000000
        },
        pricing: {
          type: 'market' as const,
          price: marketData[index]?.price || Math.random() * 500 + 50,
          currency: 'USD',
          change24h: marketData[index]?.change24h || (Math.random() - 0.5) * 10,
          changePercent24h: marketData[index]?.changePercent24h || (Math.random() - 0.5) * 5,
          volume24h: marketData[index]?.volume24h || Math.random() * 1000000
        },
        createdBy: 'system',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active' as const,
        marketData: marketData[index]
      }));
      
      setStocks(stocksWithData);
    } catch (error) {
      console.error('Failed to load stocks:', error);
    }
  };

  const loadStockDetails = async (symbol: string) => {
    try {
      const token = await unifiedTokenService.getToken(symbol);
      setSelectedStock(token as StockWithData);
    } catch (error) {
      console.error('Failed to load stock details:', error);
    }
  };

  const loadPortfolio = async () => {
    try {
      const portfolioData = await unifiedTokenService.getPortfolio();
      const stockPositions = portfolioData.balances
        .filter(b => b.token.type === 'stock')
        .map(b => ({
          ...b.token,
          userPosition: {
            shares: b.balance.amount,
            avgCost: 100, // Would come from transaction history
            currentValue: b.balance.value || 0,
            profit: (b.balance.value || 0) - (100 * b.balance.amount),
            profitPercent: ((b.balance.value || 0) - (100 * b.balance.amount)) / (100 * b.balance.amount) * 100
          }
        } as StockWithData));
      setPortfolio(stockPositions);
    } catch (error) {
      console.error('Failed to load portfolio:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const orders = await unifiedTokenService.getOrders({
        status: 'open',
        type: 'stock'
      });
      setOpenOrders(orders);
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedStock || !orderForm.shares) {
      toast({
        title: 'Invalid Order',
        description: 'Please select a stock and enter the number of shares.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const order = await unifiedTokenService.placeOrder({
        tokenId: selectedStock.id,
        side: orderForm.action,
        amount: parseFloat(orderForm.shares),
        orderType: orderForm.orderType,
        price: orderForm.orderType === 'limit' ? parseFloat(orderForm.price) : undefined,
        metadata: {
          stopPrice: orderForm.orderType === 'stop' ? parseFloat(orderForm.stopPrice) : undefined
        }
      });

      toast({
        title: 'Order Placed',
        description: `${orderForm.action === 'buy' ? 'Buy' : 'Sell'} order for ${orderForm.shares} shares of ${selectedStock.metadata.symbol} placed successfully.`
      });

      setOrderForm({
        action: 'buy',
        orderType: 'market',
        shares: '',
        price: '',
        stopPrice: ''
      });
      loadOrders();
      loadPortfolio();
    } catch (error: any) {
      toast({
        title: 'Order Failed',
        description: error.message || 'Failed to place order',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await unifiedTokenService.cancelOrder(orderId);
      toast({
        title: 'Order Cancelled',
        description: 'Your order has been cancelled successfully.'
      });
      loadOrders();
    } catch (error: any) {
      toast({
        title: 'Cancellation Failed',
        description: error.message || 'Failed to cancel order',
        variant: 'destructive'
      });
    }
  };

  const toggleWatchlist = (symbol: string) => {
    if (watchlist.includes(symbol)) {
      setWatchlist(watchlist.filter(s => s !== symbol));
    } else {
      setWatchlist([...watchlist, symbol]);
    }
  };

  const filteredStocks = stocks.filter(stock => 
    stock.metadata.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    stock.metadata.symbol?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const StockCard = ({ stock }: { stock: StockWithData }) => {
    const isPositive = (stock.pricing.changePercent24h || 0) >= 0;
    const isWatched = watchlist.includes(stock.symbol);
    
    return (
      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setSelectedStock(stock)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">{stock.metadata.symbol?.replace('S.', '')}</CardTitle>
              <p className="text-sm text-muted-foreground">{stock.metadata.company}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                toggleWatchlist(stock.symbol);
              }}
            >
              {isWatched ? <Star className="w-4 h-4 fill-yellow-500" /> : <StarOff className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">
                ${stock.pricing.price?.toFixed(2)}
              </span>
              <Badge variant={isPositive ? 'default' : 'destructive'}>
                {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {Math.abs(stock.pricing.changePercent24h || 0).toFixed(2)}%
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-muted-foreground">Volume</p>
                <p className="font-medium">{((stock.pricing.volume24h || 0) / 1000).toFixed(1)}K</p>
              </div>
              <div>
                <p className="text-muted-foreground">Sector</p>
                <p className="font-medium">{stock.metadata.sector}</p>
              </div>
            </div>
            {stock.userPosition && (
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span>Position: {stock.userPosition.shares} shares</span>
                  <Badge variant={stock.userPosition.profit >= 0 ? 'default' : 'destructive'}>
                    {stock.userPosition.profit >= 0 ? '+' : ''}{stock.userPosition.profitPercent.toFixed(2)}%
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <DevelopmentNotice feature="Stock Exchange" />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Corporate Stock Exchange</h1>
        <p className="text-muted-foreground">
          Trade fractional shares of leading companies with instant settlement
        </p>
      </div>

      <Tabs defaultValue="market" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="market">Market</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="watchlist">Watchlist</TabsTrigger>
        </TabsList>

        <TabsContent value="market" className="space-y-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search stocks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStocks.map(stock => (
              <StockCard key={stock.id} stock={stock} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="portfolio" className="space-y-6">
          {portfolio.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Positions Yet</h3>
                <p className="text-muted-foreground">
                  Start building your portfolio by buying stocks
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Portfolio Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Value</p>
                      <p className="text-2xl font-bold">
                        ${portfolio.reduce((sum, p) => sum + (p.userPosition?.currentValue || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Profit</p>
                      <p className="text-2xl font-bold text-green-600">
                        +${portfolio.reduce((sum, p) => sum + (p.userPosition?.profit || 0), 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Positions</p>
                      <p className="text-2xl font-bold">{portfolio.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Return</p>
                      <p className="text-2xl font-bold text-green-600">
                        +{(portfolio.reduce((sum, p) => sum + (p.userPosition?.profitPercent || 0), 0) / portfolio.length).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {portfolio.map(stock => (
                  <StockCard key={stock.id} stock={stock} />
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="orders" className="space-y-6">
          {openOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Open Orders</h3>
                <p className="text-muted-foreground">
                  Your executed orders will appear in your portfolio
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Open Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {openOrders.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-medium">
                          {order.side === 'buy' ? 'Buy' : 'Sell'} {order.amount} shares
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order.tokenId.replace('S.', '')} • {order.orderType} @ ${order.price || 'Market'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(order.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={order.status === 'open' ? 'default' : 'secondary'}>
                          {order.status}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="watchlist" className="space-y-6">
          {watchlist.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Stocks in Watchlist</h3>
                <p className="text-muted-foreground">
                  Add stocks to your watchlist to track them easily
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stocks.filter(s => watchlist.includes(s.symbol)).map(stock => (
                <StockCard key={stock.id} stock={stock} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Order Panel - Shows when stock is selected */}
      {selectedStock && (
        <Card className="fixed bottom-0 right-0 m-6 w-96 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{selectedStock.metadata.symbol?.replace('S.', '')}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedStock(null)}
              >
                ✕
              </Button>
            </div>
            <CardDescription>
              ${selectedStock.pricing.price?.toFixed(2)} • {selectedStock.metadata.company}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={orderForm.action} onValueChange={(v) => setOrderForm({...orderForm, action: v as 'buy' | 'sell'})}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="buy">Buy</TabsTrigger>
                <TabsTrigger value="sell">Sell</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="space-y-2">
              <Label>Order Type</Label>
              <Tabs value={orderForm.orderType} onValueChange={(v) => setOrderForm({...orderForm, orderType: v as any})}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="market">Market</TabsTrigger>
                  <TabsTrigger value="limit">Limit</TabsTrigger>
                  <TabsTrigger value="stop">Stop</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="space-y-2">
              <Label>Number of Shares</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={orderForm.shares}
                onChange={(e) => setOrderForm({...orderForm, shares: e.target.value})}
                step="0.01"
                min="0"
              />
            </div>

            {orderForm.orderType === 'limit' && (
              <div className="space-y-2">
                <Label>Limit Price</Label>
                <Input
                  type="number"
                  placeholder={selectedStock.pricing.price?.toFixed(2)}
                  value={orderForm.price}
                  onChange={(e) => setOrderForm({...orderForm, price: e.target.value})}
                  step="0.01"
                  min="0"
                />
              </div>
            )}

            {orderForm.orderType === 'stop' && (
              <div className="space-y-2">
                <Label>Stop Price</Label>
                <Input
                  type="number"
                  placeholder={selectedStock.pricing.price?.toFixed(2)}
                  value={orderForm.stopPrice}
                  onChange={(e) => setOrderForm({...orderForm, stopPrice: e.target.value})}
                  step="0.01"
                  min="0"
                />
              </div>
            )}

            {orderForm.shares && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Estimated Total:</span>
                      <span className="font-medium">
                        ${(parseFloat(orderForm.shares) * (selectedStock.pricing.price || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Button 
              onClick={handlePlaceOrder} 
              disabled={loading || !orderForm.shares}
              className="w-full"
              size="lg"
            >
              {loading ? 'Processing...' : `${orderForm.action === 'buy' ? 'Buy' : 'Sell'} ${orderForm.shares || '0'} Shares`}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
